import { AnalyticsEvents, analytics } from "common/analytics";
import { AddCoverDialog } from "features/bulkCoverInput";
import { useState } from "react";
import {
  AddFeedEventDialog,
  AddFeedPurchaseEventDialog,
  AddFertilizerEventDialog,
  AddFieldPreparationEventDialog,
  AddOtherEventDialog,
  AddReseedEventDialog,
  AddYieldEventDialog,
} from "./AddNewEventDialogs";

export function useAddNewEventDialogs() {
  // Dialog states for each event type
  const [reseedDialogOpen, setReseedDialogOpen] = useState(false);
  const [feedDialogOpen, setFeedDialogOpen] = useState(false);
  const [feedPurchaseDialogOpen, setFeedPurchaseDialogOpen] = useState(false);
  const [yieldDialogOpen, setYieldDialogOpen] = useState(false);
  const [fertilizerDialogOpen, setFertilizerDialogOpen] = useState(false);
  const [fieldPreparationDialogOpen, setFieldPreparationDialogOpen] =
    useState(false);
  const [coverDialogOpen, setCoverDialogOpen] = useState(false);
  const [otherDialogOpen, setOtherDialogOpen] = useState(false);

  // Key counters to force remount (reset form state) on each open
  const [dialogKeys, setDialogKeys] = useState({
    reseed: 0,
    feed: 0,
    feedPurchase: 0,
    yield: 0,
    fertilizer: 0,
    fieldPreparation: 0,
    cover: 0,
    other: 0,
  });

  const handleEventTypeClick = (eventType: string) => {
    switch (eventType) {
      case "reseed":
        analytics.track(AnalyticsEvents.BULK_RESEED_FORM_OPENED);
        setDialogKeys((k) => ({ ...k, reseed: k.reseed + 1 }));
        setReseedDialogOpen(true);
        break;
      case "feed":
        setDialogKeys((k) => ({ ...k, feed: k.feed + 1 }));
        setFeedDialogOpen(true);
        break;
      case "feedPurchase":
        setDialogKeys((k) => ({ ...k, feedPurchase: k.feedPurchase + 1 }));
        setFeedPurchaseDialogOpen(true);
        break;
      case "yield":
        setDialogKeys((k) => ({ ...k, yield: k.yield + 1 }));
        setYieldDialogOpen(true);
        break;
      case "fertilizer":
        setDialogKeys((k) => ({ ...k, fertilizer: k.fertilizer + 1 }));
        setFertilizerDialogOpen(true);
        break;
      case "fieldPreparation":
        setDialogKeys((k) => ({
          ...k,
          fieldPreparation: k.fieldPreparation + 1,
        }));
        setFieldPreparationDialogOpen(true);
        break;
      case "cover":
        analytics.track(AnalyticsEvents.BULK_COVER_INPUT_OPENED);
        setDialogKeys((k) => ({ ...k, cover: k.cover + 1 }));
        setCoverDialogOpen(true);
        break;
      case "other":
        setDialogKeys((k) => ({ ...k, other: k.other + 1 }));
        setOtherDialogOpen(true);
        break;
    }
  };

  // Check if any dialog is currently open
  const isAnyDialogOpen =
    reseedDialogOpen ||
    feedDialogOpen ||
    feedPurchaseDialogOpen ||
    yieldDialogOpen ||
    fertilizerDialogOpen ||
    fieldPreparationDialogOpen ||
    coverDialogOpen ||
    otherDialogOpen;

  const dialogs = (
    <>
      <AddReseedEventDialog
        key={`reseed-${dialogKeys.reseed}`}
        dialogOpen={reseedDialogOpen}
        setDialogOpen={setReseedDialogOpen}
      />
      <AddFeedEventDialog
        key={`feed-${dialogKeys.feed}`}
        dialogOpen={feedDialogOpen}
        setDialogOpen={setFeedDialogOpen}
      />
      <AddFeedPurchaseEventDialog
        key={`feedPurchase-${dialogKeys.feedPurchase}`}
        dialogOpen={feedPurchaseDialogOpen}
        setDialogOpen={setFeedPurchaseDialogOpen}
      />
      <AddYieldEventDialog
        key={`yield-${dialogKeys.yield}`}
        dialogOpen={yieldDialogOpen}
        setDialogOpen={setYieldDialogOpen}
      />
      <AddFertilizerEventDialog
        key={`fertilizer-${dialogKeys.fertilizer}`}
        dialogOpen={fertilizerDialogOpen}
        setDialogOpen={setFertilizerDialogOpen}
      />
      <AddFieldPreparationEventDialog
        key={`fieldPreparation-${dialogKeys.fieldPreparation}`}
        dialogOpen={fieldPreparationDialogOpen}
        setDialogOpen={setFieldPreparationDialogOpen}
      />
      <AddCoverDialog
        key={`cover-${dialogKeys.cover}`}
        dialogOpen={coverDialogOpen}
        setDialogOpen={setCoverDialogOpen}
      />
      <AddOtherEventDialog
        key={`other-${dialogKeys.other}`}
        dialogOpen={otherDialogOpen}
        setDialogOpen={setOtherDialogOpen}
      />
    </>
  );

  return {
    handleEventTypeClick,
    dialogs,
    isAnyDialogOpen,
  };
}
